import React from 'react'
import { useLocation } from 'react-router-dom'
import type { PanelState, PanelContextType } from '../types/panel.types'
import { ANIMATION_CLEANUP_MS } from '@shared/lib/animations'
import { PanelsContext } from './usePanels'

const MIN_WIDTH = 420
const MAX_WIDTH_RATIO = 0.5
const RESIZE_DEBOUNCE_MS = 100

// Sections with tabbed navigation that should close panels when switching tabs
const TABBED_SECTIONS = ['in-stores', 'subscriptions']

const getMaxWidth = () => Math.floor(window.innerWidth * MAX_WIDTH_RATIO)

function loadPanelWidth(id: string): number {
  try {
    const stored = localStorage.getItem(`panel-${id}-width`)
    return stored ? Math.max(MIN_WIDTH, parseInt(stored, 10)) : MIN_WIDTH
  } catch {
    return MIN_WIDTH
  }
}

function savePanelWidth(id: string, width: number): void {
  try {
    localStorage.setItem(`panel-${id}-width`, width.toString())
  } catch {
    // Ignore localStorage errors
  }
}

interface PanelsProviderProps {
  children: React.ReactNode
}

export function PanelsProvider({ children }: PanelsProviderProps) {
  const [panels, setPanels] = React.useState<Record<string, PanelState>>({})
  const [resizingPanel, setResizingPanel] = React.useState<string | null>(null)
  const timeoutRefs = React.useRef<Record<string, NodeJS.Timeout>>({})
  const location = useLocation()
  const prevLocationRef = React.useRef(location.pathname)

  const openPanel = React.useCallback((id: string) => {
    if (timeoutRefs.current[id]) {
      clearTimeout(timeoutRefs.current[id])
      delete timeoutRefs.current[id]
    }

    setPanels(prev => ({
      ...prev,
      [id]: {
        open: true,
        width: prev[id]?.width ?? loadPanelWidth(id),
        isResizing: false,
        isClosing: false,
        isExpanded: prev[id]?.isExpanded ?? false
      }
    }))
  }, [])

  const closePanel = React.useCallback((id: string) => {
    setPanels(prev => {
      const panel = prev[id]
      if (!panel || panel.isClosing) return prev

      return {
        ...prev,
        [id]: { ...panel, open: false, isClosing: true }
      }
    })

    if (timeoutRefs.current[id]) {
      clearTimeout(timeoutRefs.current[id])
    }

    timeoutRefs.current[id] = setTimeout(() => {
      setPanels(prev => {
        const { [id]: _, ...rest } = prev
        return rest
      })
      delete timeoutRefs.current[id]
    }, ANIMATION_CLEANUP_MS)
  }, [])

  const setWidth = React.useCallback((id: string, width: number) => {
    const constrainedWidth = Math.max(MIN_WIDTH, Math.min(getMaxWidth(), width))
    savePanelWidth(id, constrainedWidth)

    setPanels(prev => {
      const panel = prev[id]
      if (!panel) return prev
      return { ...prev, [id]: { ...panel, width: constrainedWidth } }
    })
  }, [])

  const startResize = React.useCallback((id: string) => {
    setResizingPanel(id)
    setPanels(prev => {
      const panel = prev[id]
      if (!panel) return prev
      return {
        ...prev,
        [id]: { ...panel, isResizing: true }
      }
    })
  }, [])

  const stopResize = React.useCallback((id: string) => {
    setResizingPanel(null)
    setPanels(prev => {
      const panel = prev[id]
      if (!panel) return prev
      return {
        ...prev,
        [id]: { ...panel, isResizing: false }
      }
    })
  }, [])

  const getPanel = React.useCallback((id: string) => panels[id], [panels])

  React.useEffect(() => {
    if (!resizingPanel) return

    const handleMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      setWidth(resizingPanel, window.innerWidth - e.clientX)
    }

    const handleMouseUp = () => stopResize(resizingPanel)
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') stopResize(resizingPanel)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [resizingPanel, setWidth, stopResize])

  React.useEffect(() => {
    let resizeTimeout: NodeJS.Timeout

    const handleWindowResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        const maxWidth = getMaxWidth()
        setPanels(prev => {
          let hasChanges = false
          const updated = { ...prev }

          Object.keys(updated).forEach(id => {
            if (updated[id].width > maxWidth) {
              updated[id] = { ...updated[id], width: maxWidth }
              savePanelWidth(id, maxWidth)
              hasChanges = true
            }
          })

          return hasChanges ? updated : prev
        })
      }, RESIZE_DEBOUNCE_MS)
    }

    window.addEventListener('resize', handleWindowResize)
    return () => {
      clearTimeout(resizeTimeout)
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  const shouldClosePanels = React.useCallback((currentPath: string, previousPath: string) => {
    const [, currentBase, currentSub] = currentPath.split('/')
    const [, prevBase, prevSub] = previousPath.split('/')

    return (
      // Different base routes
      (prevBase && currentBase !== prevBase) ||
      // Different sub-paths within tabbed sections
      (TABBED_SECTIONS.includes(currentBase) && prevSub && currentSub !== prevSub) ||
      // Navigating to a detail page
      (currentPath.match(/\/[^/]+\/[^/]+$/) && !currentPath.includes('?selectedEntity'))
    )
  }, [])

  const closeAllPanels = React.useCallback(() => {
    setPanels(prev => {
      const updates: Record<string, PanelState> = {}
      let hasChanges = false

      Object.entries(prev).forEach(([id, panel]) => {
        if (panel.open && !panel.isClosing) {
          updates[id] = { ...panel, open: false, isClosing: true }
          hasChanges = true

          if (timeoutRefs.current[id]) {
            clearTimeout(timeoutRefs.current[id])
          }

          timeoutRefs.current[id] = setTimeout(() => {
            setPanels(p => {
              const { [id]: _, ...rest } = p
              return rest
            })
            delete timeoutRefs.current[id]
          }, ANIMATION_CLEANUP_MS)
        }
      })

      return hasChanges ? { ...prev, ...updates } : prev
    })
  }, [])

  React.useEffect(() => {
    const currentPath = location.pathname
    const previousPath = prevLocationRef.current

    if (shouldClosePanels(currentPath, previousPath)) {
      closeAllPanels()
    }

    prevLocationRef.current = currentPath
  }, [location.pathname, shouldClosePanels, closeAllPanels])

  // Clean up timeouts on unmount
  React.useEffect(() => {
    return () => {
      Object.values(timeoutRefs.current).forEach(clearTimeout)
      timeoutRefs.current = {}
    }
  }, [])

  const contextValue = React.useMemo<PanelContextType>(() => ({
    panels,
    openPanel,
    closePanel,
    setWidth,
    startResize,
    stopResize,
    getPanel
  }), [panels, openPanel, closePanel, setWidth, startResize, stopResize, getPanel])

  return (
    <PanelsContext.Provider value={contextValue}>
      {children}
    </PanelsContext.Provider>
  )
}
